import { useState } from 'react';
import type { MouseEvent } from 'react';
import MoreVert from '@mui/icons-material/MoreVert';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import {
  PrimaryNavigationMenu,
  PrimaryNavigationMenuItem,
} from 'components/ui/PrimaryNavigation';
import {
  FileRowActionButton,
  FileRowActionsInlineGroup,
  FileRowActionsRoot,
} from './FileRowActions.styles';
import type { FileRowActionsProps } from './FileRowActions.types';

const FileRowActions = ({
  actions,
  ariaLabel,
  className,
  testId,
  zoomLevel,
}: FileRowActionsProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isMenuOpen = Boolean(anchorEl);
  const menuId = testId ? `${testId}-menu` : undefined;
  const regularMenuActions = actions.filter(action => action.tone !== 'danger');
  const dangerMenuActions = actions.filter(action => action.tone === 'danger');
  const menuActions = [...regularMenuActions, ...dangerMenuActions];
  const shouldShowDangerDivider = regularMenuActions.length > 0 && dangerMenuActions.length > 0;
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const renderActionIcon = (action: FileRowActionsProps['actions'][number]) => (
    action.isLoading ? (
      <CircularProgress
        aria-hidden="true"
        color="inherit"
        size="var(--icon-button-compact-icon-size)"
        thickness={5}
      />
    ) : action.icon
  );

  return (
    <FileRowActionsRoot
      aria-label={ariaLabel}
      className={className}
      data-row-action
      data-testid={testId}
      role="group"
      zoomLevel={zoomLevel}>
      <FileRowActionsInlineGroup data-file-row-actions-inline="true">
        {actions.map(action => (
          <FileRowActionButton
            key={action.id}
            aria-label={action.ariaLabel}
            aria-busy={action.isLoading || undefined}
            actionTone={action.tone}
            data-testid={action.testId}
            disabled={action.disabled || action.isLoading}
            onClick={action.onClick}
            size="small"
            tooltipLabel={action.tooltipLabel ?? action.ariaLabel}
            variant="ghost"
            visualSize="compact">
            {renderActionIcon(action)}
          </FileRowActionButton>
        ))}
      </FileRowActionsInlineGroup>
      <FileRowActionButton
        aria-controls={isMenuOpen ? menuId : undefined}
        aria-expanded={isMenuOpen || undefined}
        aria-haspopup="menu"
        aria-label={ariaLabel}
        data-file-row-actions-menu-trigger="true"
        disabled={actions.every(action => action.disabled || action.isLoading)}
        onClick={(event: MouseEvent<HTMLButtonElement>) => {
          event.stopPropagation();
          setAnchorEl(event.currentTarget);
        }}
        size="small"
        tooltipLabel={ariaLabel}
        variant="ghost"
        visualSize="compact">
        <MoreVert fontSize="inherit" />
      </FileRowActionButton>
      <PrimaryNavigationMenu
        anchorEl={anchorEl}
        disableRestoreFocus
        id={menuId}
        menuMinWidth={0}
        menuWidth="content"
        onClick={event => event.stopPropagation()}
        onClose={handleMenuClose}
        open={isMenuOpen}>
        {menuActions.map((action, index) => [
          shouldShowDangerDivider && index === regularMenuActions.length ? <Divider key="danger-divider" /> : null,
          <PrimaryNavigationMenuItem
            key={action.id}
            data-testid={action.testId ? `${action.testId}-menu-item` : undefined}
            disabled={action.disabled || action.isLoading}
            leadingSlot={renderActionIcon(action)}
            onClick={event => {
              handleMenuClose();
              action.onClick(event);
            }}
            tone={action.tone}>
            {action.tooltipLabel ?? action.ariaLabel}
          </PrimaryNavigationMenuItem>,
        ])}
      </PrimaryNavigationMenu>
    </FileRowActionsRoot>
  );
};

export default FileRowActions;
